import { Component, OnInit, Output, EventEmitter, OnDestroy, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, FormArray } from '@angular/forms';
import * as moment from 'moment-timezone';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { DriversService } from 'src/app/component/drivers/drivers-service/drivers-service';
import { RouteService } from '../service/route.service';
import { FleetService } from 'src/app/component/fleet/fleet-service/fleet.service';

@Component({
  selector: 'app-add-route-modal',
  templateUrl: './add-route-modal.component.html',
  styleUrls: ['./add-route-modal.component.css']
})
export class AddRouteModalComponent implements OnInit, OnDestroy {
  @Input() id;
  @Output() close = new EventEmitter<void>();
  @Output() onSaveRoute = new EventEmitter<any>();
  routeForm!: FormGroup;
  submitted = false;
  user;
  timeZone;
  drivers;
  fleetsMap: any = {};
  addressPickerObj = null;

  constructor(
    private routeService: RouteService,
    private driverService: DriversService,
    private fleetService: FleetService,
    private sharedDataService: SharedDataService,
    private fb: FormBuilder
  ) { }

  ngOnInit() {
    this.initForm();
    this.getUser();
    if (this.id) {
      this.getRouteById();
    }
  }

  initForm() {
    this.routeForm = this.fb.group({
      routeName: ['', Validators.required],
      routeCode: ['', Validators.required],

      preTripDate: [''],
      tripDate: ['', Validators.required],
      notes: [''],
      driver: [null, Validators.required],
      fleet: [null],
      preScheduledStartTime: [''],
      scheduledStartTime: ['', Validators.required],
      status: ['pending'],
      stops: this.fb.array([], [this.minStopsValidator(2)])
    });
  }

  minStopsValidator(min: number) {
    return (control: any) => {
      return control && control.length >= min
        ? null
        : { minStops: true };
    };
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(user => {
        if (user) {
          this.user = user;
          this.timeZone = this.user.timeZone;
          this.routeForm.patchValue({
            preTripDate: moment().tz(this.timeZone).startOf('day').toISOString(),
            tripDate: moment().tz(this.timeZone).startOf('day').toISOString(),

            preScheduledStartTime: moment().tz(this.timeZone).startOf('day').toISOString(),
            scheduledStartTime: moment().tz(this.timeZone).startOf('day').format('HH:mm')
          });
          this.getDrivers();
        }
      });
  }

  getDrivers() {
    this.driverService.getDrivers(this.user._id, 'active')
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          const drivers = Array.isArray(data) ? data : (data && data.data ? data.data : []);
          if (drivers.length) {
            this.fleetService.getFleetList({ company: this.user._id })
              .pipe(takeUntil(componentDestroyed(this)))
              .subscribe(fRes => {
                const fleets = Array.isArray(fRes) ? fRes : (fRes && fRes.data ? fRes.data : []);
                this.fleetsMap = {};
                fleets.forEach(f => this.fleetsMap[f._id] = f);

                this.drivers = drivers.map(d => {
                  const fleetId = (d.fleet && typeof d.fleet === 'object') ? d.fleet._id : d.fleet;
                  const fleetInfo = this.fleetsMap[fleetId] || (typeof d.fleet === 'object' ? d.fleet : null);
                  return {
                    ...d,
                    vehicleInfo: fleetInfo
                  };
                });
              });
          }
        },
        (err) => {
          console.log('Error fetching drivers', err);
        }
      );
  }

  onDriverChange(driver) {
    if (driver) {
      const fleetId = (driver.fleet && typeof driver.fleet === 'object') ? driver.fleet._id : driver.fleet;
      this.routeForm.patchValue({
        fleet: fleetId
      });
    } else {
      this.routeForm.patchValue({
        fleet: null
      });
    }
  }

  getRouteById() {
    this.routeService.getRoutesbyId(this.id)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data) {
            this.routeForm.patchValue({
              ...data,
              preTripDate: moment(data.tripDate).tz(this.timeZone).toISOString(),
              preScheduledStartTime: moment(data.scheduledStartAt).tz(this.timeZone).toISOString(),
            });
            if (data.stops && data.stops.length) {
              this.addStop(data.stops);
            }
          }
        }
      );
  }

  addStop(stops?) {
    if (stops) {
      for (let i = 0; i < stops.length; i++) {
        const stop = stops[i];
        this.stops.push(
          this.fb.group({
            name: [stop.name, Validators.required],
            address: [stop.address, Validators.required],
            latitude: [stop.latitude, Validators.required],
            longitude: [stop.longitude, Validators.required],
            waitTime: [stop.waitTime, Validators.required],
            status: [stop.status || 'pending'],
            sequence: [stop.sequence || i + 1],
          }));
      }
      return;
    }
    const nextSequence = this.stops.length + 1;
    this.stops.push(
      this.fb.group({
        name: ['', Validators.required],
        address: ['', Validators.required],
        latitude: [null, Validators.required],
        longitude: [null, Validators.required],
        waitTime: [0, Validators.required],
        status: ['pending'],
        sequence: [nextSequence],
      }));
  }

  removeStop(index: number) {
    const stop = this.stops.at(index);
    if (stop && stop.get('status').value !== 'pending') {
      return;
    }
    this.stops.removeAt(index);
  }

  onDateChange(evt, control: AbstractControl) {
    if (evt) {
      control.setValue(moment(evt).toISOString());
    }
  }

  onTimeChange(evt, control: AbstractControl) {
    if (evt) {
      control.setValue(moment(evt).format('HH:mm'));
    }
  }

  setStopAddress(place, index) {
    this.stops.at(index).patchValue({
      address: place.formatted_address,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
    });
  }

  onSubmit() {
    this.submitted = true;
    if (this.routeForm.invalid) {
      return;
    }

    this.routeService.saveRoutes(this.id, this.routeForm.value)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data) {
            this.onSaveRoute.emit(data);
          }
        },
        (err) => {
          console.log('Error saving route', err);
        }
      );
  }

  closeModal() {
    this.close.emit();
  }

  get form() {
    return this.routeForm.controls;
  }

  get stops(): FormArray {
    return this.routeForm.get('stops') as FormArray;
  }

  loadAddressPicker(title, value, address?, lat?, lng?, zipCode?) {
    this.addressPickerObj = { title, value, address, lat, lng, zipCode };
  }

  onAddressPicker(event) {
    this.addressPickerObj = null;
    if (!event) return;

    const stopIndex = parseInt(event.reference.value);
    if (!isNaN(stopIndex)) {
      const stopGroup = this.stops.at(stopIndex) as FormGroup;
      stopGroup.patchValue({
        address: event.address,
        latitude: event.lat,
        longitude: event.lng
      });
    }
  }

  getRigNumber(item: any): string {
    if (!item) return '';
    let fleetId = item.fleet;
    if (item.fleet && typeof item.fleet === 'object') {
      fleetId = item.fleet._id;
    }
    const fleet = item.vehicleInfo || this.fleetsMap[fleetId];
    return (fleet && fleet.setiDecall) ? fleet.setiDecall : '';
  }

  ngOnDestroy() {
  }
}
